import type { AuthError, EventMessage } from '@azure/msal-browser'
import { EventType } from '@azure/msal-browser'
import type { IPublicClientApplication } from '@azure/msal-browser'
import { trackAuthEvent } from './telemetry'
import { parseAadstsCode } from './authErrors'

/**
 * Issue #43 — capture de l'échec du retour de redirection, AVANT le premier rendu.
 *
 * Pourquoi ce module existe, et pourquoi `useMsalAuthentication` ne suffit pas :
 *
 * 1. `MsalProvider` démarre en `InteractionStatus.Startup` et n'appelle `handleRedirectPromise()`
 *    que dans son `useEffect`, donc APRÈS le premier commit.
 * 2. `main.tsx` ne rend rien tant que `inProgress !== None` — c'est la règle `stack-msal.md`, et
 *    elle est juste : rendre pendant le traitement du retour produit une page blanche.
 * 3. Au rejet, `msal-browser` émet `LOGIN_FAILURE` **puis** `HANDLE_REDIRECT_END`, ce dernier
 *    seul faisant passer `inProgress` à `None`.
 * 4. `useMsalAuthentication` ne reçoit son erreur que par `addEventCallback`
 *    (`useMsalAuthentication.js:140-154`), et `emitEvent` ne notifie que les abonnés **déjà
 *    enregistrés** : `addEventCallback` ne rejoue jamais un événement passé.
 *
 * ⇒ Le composant s'abonne systématiquement APRÈS l'émission, ne voit rien, et relance un login :
 * la boucle #43 survivrait au correctif. L'abonnement doit donc être posé **avant `createRoot`**,
 * quand la fenêtre d'écoute est encore ouverte.
 *
 * Constat vérifié dans `node_modules/@azure/msal-react@2.2.0` et confirmé par la gate
 * `shared-contract-guardrail` du 2026-08-29 (risque R1, bloquant).
 */

let bootError: AuthError | null = null

/**
 * À appeler une seule fois, **avant** `createRoot(...)`.
 *
 * On écoute les deux familles d'échec, et pas seulement `LOGIN_FAILURE` :
 * `handleRedirectPromise` n'émet `LOGIN_FAILURE` que si `getAllAccounts()` est **vide**. Quand un
 * compte est encore en cache — consentement révoqué en cours de vie, par exemple — c'est
 * `ACQUIRE_TOKEN_FAILURE` qui sort, et personne ne l'écoutait.
 */
export function captureRedirectErrors(instance: IPublicClientApplication): void {
  instance.addEventCallback((message: EventMessage) => {
    switch (message.eventType) {
      case EventType.LOGIN_FAILURE:
      case EventType.ACQUIRE_TOKEN_FAILURE:
      case EventType.SSO_SILENT_FAILURE:
        if (message.error) {
          const err = message.error as AuthError
          bootError = err
          // Point d'émission UNIQUE de la télémétrie d'échec d'authentification. Le placer ici
          // plutôt que dans l'écran couvre aussi les échecs de l'arbre authentifié, qu'aucun
          // composant ne rend. Seul le code part : `errorMessage` porte le `tid`, que le contrat
          // de télémétrie interdit (§1, no-PII).
          trackAuthEvent('auth_redirect_error', {
            reason: parseAadstsCode(err.errorMessage) ?? err.errorCode ?? 'unknown',
          })
        }
        break
      // Un succès, quel qu'il soit, prouve que la cause du blocage a disparu.
      case EventType.LOGIN_SUCCESS:
      case EventType.ACQUIRE_TOKEN_SUCCESS:
      case EventType.SSO_SILENT_SUCCESS:
        bootError = null
        break
    }
  })
}

/** L'erreur retenue au démarrage, ou `null`. */
export function getRedirectError(): AuthError | null {
  return bootError
}

/** Oublie l'erreur — appelé avant une nouvelle tentative explicite de l'utilisateur. */
export function clearRedirectError(): void {
  bootError = null
}
